import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LocationAutocomplete from "../shared/LocationAutocomplete";
import {
  DEFAULT_SEARCH_SHIFT,
  DEFAULT_SEARCH_TIME,
  SEARCH_SHIFT_OPTIONS,
  formatSearchTimeLabel,
  getMarketplaceTodayIsoDate,
  getMarketplaceMaxIsoDate,
  getSearchShiftOption,
  getSearchTimeOptionsForShift,
} from "../../config/marketplaceFilterEngine";
import { usePlatformConfig } from "../../context/PlatformConfigContext";
import { formatCaregiverRoleStatusLine, isCaregiverVerified } from "../../utils/mapMarketplaceCaregiver";
import ProfileAvatar from "../shared/ProfileAvatar";
import { resolveIndianCity } from "../../config/indianCities";
import CareTypeTabIcon from "./CareTypeTabIcons";
import HourlyPreSearchPanel from "./HourlyPreSearchPanel";
import "./MarketplaceBrowseUI.css";

const SERVICE_TABS = [
  {
    id: "attendant",
    label: "Care Attendant",
  },
  {
    id: "nurses",
    label: "Nurse",
  },
  {
    id: "physio",
    label: "Physiotherapist",
  },
];

function ServiceStatusBadge({ status, label }) {
  if (!status && !label) return null;
  const isLive = String(status || "").toLowerCase() === "live";
  const text = label || (isLive ? "LIVE" : "COMING SOON");
  return (
    <span
      className={`landing-search-unified__tab-badge${isLive ? " is-live" : " is-soon"}`}
      aria-label={text}
    >
      {text}
    </span>
  );
}

function getSearchDateParts(isoDate) {
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    return { main: isoDate || "", weekday: "" };
  }
  return {
    main: d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }),
    weekday: d.toLocaleDateString("en-IN", { weekday: "long" }),
  };
}

function SearchIconPin() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" aria-hidden>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function SearchIconCalendar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function SearchIconClock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" strokeLinecap="round" />
    </svg>
  );
}

function SearchIconHours() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" aria-hidden>
      <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3" strokeLinecap="round" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}

function SearchChevronDown() {
  return (
    <svg className="landing-search-unified__caret" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="m2.5 4.5 3.5 3.5L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SearchIconNearMe() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
    </svg>
  );
}

function SearchIconMagnifier() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function SearchFieldDivider() {
  return <div className="landing-search-unified__divider" aria-hidden />;
}

export function MarketplaceHero() {
  const { getContent } = usePlatformConfig();
  const content = getContent?.() || {};
  const headline = content.marketplaceTitle || "Find trusted caregivers, nurses & physiotherapists for your loved ones.";
  const subline = content.marketplaceSubtitle || "Verified. Background Checked. Reliable.";
  return (
    <section className="landing-hero-strip" aria-label="Marketplace trust message">
      <div className="landing-hero-strip__inner">
        <span className="landing-hero-strip__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2.5 5 5.75v6.1c0 4.35 3 8.4 7 9.65 4-1.25 7-5.3 7-9.65v-6.1L12 2.5z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <path
              d="m9.2 12.1 1.8 1.8 3.8-3.8"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <p>
          {headline}
          {subline ? <> {subline}</> : null}
        </p>
      </div>
    </section>
  );
}

function getShiftLabel(value) {
  return getSearchShiftOption(value).label;
}

function SearchPickerDropdown({
  pickerId,
  open,
  onToggle,
  label,
  icon,
  children,
  prompt = false,
  className = "",
  valueDisplay,
  ignoreOutsideUntilRef,
}) {
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) {
      setPanelStyle(null);
      return undefined;
    }

    function updatePosition() {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setPanelStyle({
        position: "fixed",
        top: rect.bottom + 6,
        left: rect.left,
        width: Math.max(rect.width, 220),
        zIndex: 1300,
      });
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, label]);

  useEffect(() => {
    if (!open) return undefined;
    let removeListener = null;
    const timer = window.setTimeout(() => {
      function onDocClick(e) {
        if (ignoreOutsideUntilRef?.current && Date.now() < ignoreOutsideUntilRef.current) return;
        const inTrigger = wrapRef.current?.contains(e.target);
        const inPanel = panelRef.current?.contains(e.target);
        if (!inTrigger && !inPanel) {
          onToggle(false);
        }
      }
      document.addEventListener("click", onDocClick, true);
      removeListener = () => document.removeEventListener("click", onDocClick, true);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      removeListener?.();
    };
  }, [open, onToggle, ignoreOutsideUntilRef]);

  function handleTriggerClick(e) {
    e.stopPropagation();
    if (!open) ignoreOutsideUntilRef.current = 0;
    onToggle(!open);
  }

  const panel =
    open && panelStyle ? (
      <div
        ref={panelRef}
        id={pickerId}
        className="landing-search-unified__picker-panel landing-search-unified__picker-panel--portal"
        style={panelStyle}
        role="listbox"
      >
        {children}
      </div>
    ) : null;

  return (
    <div
      ref={wrapRef}
      className={`landing-search-unified__field landing-search-unified__field--picker landing-search-unified__field--dropdown${open ? " is-open is-active" : ""}${
        prompt && !open ? " is-active landing-search-unified__field--prompt" : ""
      } ${className}`.trim()}
    >
      <button
        type="button"
        className="landing-search-unified__picker-trigger"
        aria-expanded={open}
        aria-controls={pickerId}
        onClick={handleTriggerClick}
      >
        <span className="landing-search-unified__icon" aria-hidden>
          {icon}
        </span>
        <div className="landing-search-unified__field-body">
          <span className="landing-search-unified__label">{label}</span>
          {valueDisplay}
        </div>
        <SearchChevronDown />
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </div>
  );
}

function SearchPickerOption({ selected, onSelect, children, disabled = false, disabledHint = "" }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      aria-disabled={disabled}
      disabled={disabled}
      title={disabled ? disabledHint : undefined}
      className={`landing-search-unified__picker-option${selected ? " is-selected" : ""}${
        disabled ? " is-disabled" : ""
      }`}
      onClick={disabled ? undefined : onSelect}
    >
      {children}
      {disabled && disabledHint ? (
        <span className="landing-search-unified__picker-option-hint">{disabledHint}</span>
      ) : null}
    </button>
  );
}

export function MarketplaceSearchBar({
  serviceTab,
  onServiceTabChange,
  careTypeEngaged,
  onCareTypeEngaged,
  careTypeTabs = SERVICE_TABS,
  locationLabel,
  onLocationChange,
  onLocationSelect,
  searchDate,
  onSearchDateChange,
  searchTime = DEFAULT_SEARCH_TIME,
  onSearchTimeChange,
  searchShift = DEFAULT_SEARCH_SHIFT,
  onSearchShiftChange,
  searchShiftOptions = SEARCH_SHIFT_OPTIONS,
  /** @deprecated use searchShift */
  searchHours,
  onSearchHoursChange,
  onSearch,
  promptLocation = false,
  promptDate = false,
  locationComplete = false,
  canSearch = false,
  nearMeActive = false,
  nearMeLoading = false,
  onNearMe,
  /** When true, close any open date/time/shift panels (e.g. while a blocking modal is shown). */
  suppressPickers = false,
  /** Increment to force location into edit mode (e.g. Try Another Location). */
  locationEditSignal = 0,
  /** Hourly pre-search wizard (patient + pass already OK). */
  showHourlyWizard = false,
  hourlyPackageKey = "hygiene_personal",
  onHourlyPackageKeyChange,
  hourlyPattern = "single",
  onHourlyPatternChange,
  hourlyRecurringPattern = "daily",
  onHourlyRecurringPatternChange,
  hourlyAvailableSlots = [],
  hourlySelectedSlot = null,
  onHourlySelectSlot,
  hourlySlotsReady = false,
  hourlyLoadingSlots = false,
  onHourlyGenerateSlots,
  canGenerateHourlySlots = false,
  /** When hourly + patient ready, submit needs a selected slot. */
  hourlySubmitReady = false,
  hourlyPatientReady = false,
}) {
  const resolvedShift =
    typeof searchShift === "string" && searchShift
      ? searchShift
      : getSearchShiftOption(searchHours).value;
  const setShift = onSearchShiftChange || onSearchHoursChange;

  const [openPicker, setOpenPicker] = useState(null);
  const [locationListOpen, setLocationListOpen] = useState(false);
  const [locationFocused, setLocationFocused] = useState(false);
  /** Once a location is committed, keep it fixed until Change / Try Another Location. */
  const [locationEditing, setLocationEditing] = useState(!locationComplete);
  const ignoreOutsideUntilRef = useRef(0);
  const suppressPickersRef = useRef(suppressPickers);
  suppressPickersRef.current = suppressPickers;
  const lastLocationEditSignalRef = useRef(locationEditSignal);

  useEffect(() => {
    if (!suppressPickers) return;
    setOpenPicker(null);
    setLocationListOpen(false);
    setLocationFocused(false);
  }, [suppressPickers]);

  useEffect(() => {
    if (!locationComplete) setLocationEditing(true);
  }, [locationComplete]);
  const dateParts = searchDate ? getSearchDateParts(searchDate) : null;
  const timeLabel = formatSearchTimeLabel(searchTime);
  const shiftLabel = getShiftLabel(resolvedShift);
  const isHourlyShift = String(resolvedShift).toLowerCase() === "hourly";
  // Each shift type has its own valid start-time window (Hourly 7 AM-9 PM,
  // 12 Hrs Day/Night fixed windows, 24h live-in 8 AM-6 PM) — recompute options
  // whenever the shift changes instead of showing one flat, shift-agnostic list.
  // Also drops already-passed times when the selected date is today.
  const searchTimeOptions = getSearchTimeOptionsForShift(resolvedShift, searchDate);
  useEffect(() => {
    if (searchTimeOptions.length) {
      if (!searchTimeOptions.some((opt) => opt.value === searchTime)) {
        onSearchTimeChange?.(searchTimeOptions[0].value);
      }
      return;
    }
    // Every start time for this shift has already passed today (e.g. it's evening
    // and 12hr-day only offers 7/8/9am starts). The Shift Type option is disabled
    // for today in that case so this shouldn't normally be reachable — but if time
    // simply passes while this shift stays selected, clear the stale time instead
    // of silently jumping the date to tomorrow (that surprised customers who had
    // deliberately chosen today).
    if (searchTime) onSearchTimeChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check when the shift/date (and thus options) change
  }, [resolvedShift, searchDate]);
  // The selected shift itself can go stale (e.g. it was valid when picked, or is
  // the page's default, and start times for it have since passed today) — block
  // search rather than let it submit with no real time slot.
  const currentShiftBlockedToday =
    searchDate === getMarketplaceTodayIsoDate() && searchTimeOptions.length === 0;
  const submitLabel = isHourlyShift
    ? "Book Hourly"
    : hourlyPatientReady
      ? "Search Caregivers"
      : "Select Patient";
  const submitEnabled = canSearch && !currentShiftBlockedToday;
  const submitTitle = currentShiftBlockedToday
    ? "No slots left today for this shift — pick another shift or date"
    : !canSearch
      ? isHourlyShift
        ? "Select care type, location, and start date to book hourly"
        : "Select care type, location, and start date to search"
      : isHourlyShift && !hourlyPatientReady
        ? "Select a patient, then package and time slot in the popup"
        : isHourlyShift && hourlyPatientReady && !hourlySubmitReady
          ? "Finish package and time slot in the popup, then search"
          : undefined;

  const openNextPicker = useCallback((picker) => {
    if (suppressPickersRef.current) return;
    ignoreOutsideUntilRef.current = Date.now() + 500;
    window.setTimeout(() => {
      if (suppressPickersRef.current) return;
      setOpenPicker(picker);
    }, 100);
  }, []);

  const setDateOpen = useCallback((next) => {
    if (next && suppressPickersRef.current) return;
    if (next) ignoreOutsideUntilRef.current = 0;
    setOpenPicker(next ? "date" : null);
  }, []);

  const setTimeOpen = useCallback((next) => {
    if (next && suppressPickersRef.current) return;
    if (next) ignoreOutsideUntilRef.current = 0;
    setOpenPicker(next ? "time" : null);
  }, []);

  const setShiftOpen = useCallback((next) => {
    if (next && suppressPickersRef.current) return;
    if (next) ignoreOutsideUntilRef.current = 0;
    setOpenPicker(next ? "shift" : null);
  }, []);

  function handleLocationChange(label) {
    onLocationChange?.(label);
  }

  function focusLocationInput({ selectAll = false } = {}) {
    const input = document.querySelector(".landing-search-unified__field--location input");
    if (!input) return;
    input.focus();
    if (selectAll) {
      try {
        input.select();
      } catch {
        /* ignore */
      }
    }
  }

  function beginLocationEdit() {
    if (suppressPickersRef.current) return;
    setLocationEditing(true);
    setOpenPicker(null);
    window.setTimeout(() => focusLocationInput({ selectAll: true }), 50);
  }

  useEffect(() => {
    if (locationEditSignal === lastLocationEditSignalRef.current) return;
    lastLocationEditSignalRef.current = locationEditSignal;
    if (!locationEditSignal) return;
    setLocationEditing(true);
    setOpenPicker(null);
    window.setTimeout(() => focusLocationInput({ selectAll: true }), 80);
  }, [locationEditSignal]);

  async function handleNearMeClick() {
    if (nearMeLoading) return;
    onCareTypeEngaged?.(true);
    try {
      const ok = await onNearMe?.();
      if (ok) {
        setLocationEditing(false);
        setLocationFocused(false);
        setLocationListOpen(false);
        openNextPicker("date");
      }
    } catch (err) {
      console.error("Near me failed:", err);
    }
  }

  function handleLocationCommit(label) {
    const trimmed = String(label || "").trim();
    if (!trimmed) return;
    const resolved = resolveIndianCity(trimmed);
    // Accept aliases (e.g. Bangalore → Bengaluru, Karnataka) — do not require exact label match.
    if (!resolved) return;
    if (locationComplete && locationLabel === resolved.label) {
      setLocationEditing(false);
      return;
    }
    handleLocationPick(resolved);
  }

  function handleLocationPick(city) {
    if (!city) return;
    onLocationSelect?.(city);
    setLocationEditing(false);
    setLocationFocused(false);
    setLocationListOpen(false);
    openNextPicker("date");
  }

  function handleDatePick(value) {
    onSearchDateChange?.(value);
    setOpenPicker(null);
    if (value) {
      openNextPicker("shift");
    }
  }

  function handleTimePick(value) {
    onSearchTimeChange?.(value);
    setOpenPicker(null);
  }

  function handleShiftPick(value) {
    setShift?.(value);
    setOpenPicker(null);
    openNextPicker("time");
  }

  function handleCareTypeSelect(id) {
    onServiceTabChange(id);
    onCareTypeEngaged(true);
    if (!locationComplete) {
      window.setTimeout(() => beginLocationEdit(), 120);
    }
  }

  const locationLocked = locationComplete && !locationEditing;
  const locationActive = promptLocation || locationFocused || locationListOpen || locationEditing;
  const activeTabIndex = Math.max(0, careTypeTabs.findIndex((t) => t.id === serviceTab));
  const isFirstActiveTab = activeTabIndex === 0;
  const isLastActiveTab = activeTabIndex === careTypeTabs.length - 1;

  return (
    <section className={`landing-search-unified${careTypeEngaged ? " landing-search-unified--engaged" : ""}`}>
      <div
        className="landing-search-unified__shell"
        data-active-tab={activeTabIndex}
        data-active-tab-first={isFirstActiveTab ? "true" : undefined}
        data-active-tab-last={isLastActiveTab ? "true" : undefined}
      >
        <div className="landing-search-unified__tabs" role="tablist" aria-label="Care type">
          {careTypeTabs.map(({ id, label, status, statusLabel, bookingEnabled = true }) => {
            const isComingSoon = bookingEnabled === false || String(status || "").toLowerCase() === "coming_soon";
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={serviceTab === id}
                aria-label={`${label}${statusLabel ? `, ${statusLabel}` : ""}`}
                aria-disabled={isComingSoon ? "true" : undefined}
                className={`landing-search-unified__tab${serviceTab === id ? " is-active" : ""}${
                  isComingSoon ? " is-coming-soon" : ""
                }`}
                onClick={() => {
                  if (isComingSoon) {
                    onServiceTabChange(id);
                    onCareTypeEngaged?.(true);
                    return;
                  }
                  handleCareTypeSelect(id);
                }}
              >
                <CareTypeTabIcon type={id} />
                <span className="landing-search-unified__tab-label">{label}</span>
                <ServiceStatusBadge status={status} label={statusLabel} />
              </button>
            );
          })}
        </div>
        <div className="landing-search-unified__band">
          <div className="landing-search-unified__band-pattern" aria-hidden="true" />
          <p className="landing-search-unified__band-tagline">
            India's first direct-hire home care marketplace — find verified caregivers and hire them directly.
          </p>
          <div className="landing-search-unified__row">
            <div className="landing-search-unified__row-inner">
          <div
            className={`landing-search-unified__field landing-search-unified__field--location${
              locationActive ? " is-active" : ""
            }${promptLocation ? " landing-search-unified__field--prompt" : ""}`}
          >
            <span className="landing-search-unified__icon" aria-hidden>
              <SearchIconPin />
            </span>
            <div className="landing-search-unified__field-body landing-search-unified__field-body--location">
              <span className="landing-search-unified__label">Location</span>
              <LocationAutocomplete
                value={locationLabel}
                onChange={handleLocationChange}
                onSelect={handleLocationPick}
                onCommit={handleLocationCommit}
                onOpenChange={setLocationListOpen}
                onFocusChange={setLocationFocused}
                readOnly={locationLocked}
                onReadOnlyActivate={beginLocationEdit}
                placeholder="City, state"
              />
            </div>
            <div className="landing-search-unified__location-actions">
              <button type="button" className="landing-search-unified__change" onClick={beginLocationEdit}>
                Change
              </button>
              <button
                type="button"
                className={`landing-search-unified__near-me${nearMeActive ? " is-active" : ""}`}
                onClick={handleNearMeClick}
                disabled={nearMeLoading}
                aria-pressed={nearMeActive}
              >
                <SearchIconNearMe />
                {nearMeLoading ? "Locating…" : "Near me"}
              </button>
            </div>
          </div>
          <SearchFieldDivider />
          <SearchPickerDropdown
            pickerId="search-picker-date"
            open={openPicker === "date"}
            onToggle={setDateOpen}
            ignoreOutsideUntilRef={ignoreOutsideUntilRef}
            label="Date"
            icon={<SearchIconCalendar />}
            className="landing-search-unified__field--date"
            prompt={promptDate && openPicker !== "date"}
            valueDisplay={
              dateParts ? (
                <div className="landing-search-unified__value-stack">
                  <strong>{dateParts.main}</strong>
                  {dateParts.weekday ? <small>{dateParts.weekday}</small> : null}
                </div>
              ) : (
                <strong className="landing-search-unified__value-placeholder">Select date</strong>
              )
            }
          >
            <label className="landing-search-unified__picker-date-input">
              <span>Pick a date</span>
              <input
                type="date"
                value={searchDate || ""}
                min={getMarketplaceTodayIsoDate()}
                max={getMarketplaceMaxIsoDate()}
                onChange={(e) => handleDatePick(e.target.value)}
              />
            </label>
          </SearchPickerDropdown>
          <SearchFieldDivider />
          <SearchPickerDropdown
            pickerId="search-picker-shift"
            open={openPicker === "shift"}
            onToggle={setShiftOpen}
            ignoreOutsideUntilRef={ignoreOutsideUntilRef}
            label="Shift Type"
            icon={<SearchIconHours />}
            className="landing-search-unified__field--hours"
            prompt={false}
            valueDisplay={
              <div className="landing-search-unified__value-stack">
                <strong className="landing-search-unified__value-main">{shiftLabel}</strong>
              </div>
            }
          >
            {searchShiftOptions.map((opt) => {
              const isToday = searchDate === getMarketplaceTodayIsoDate();
              const blockedToday =
                isToday && getSearchTimeOptionsForShift(opt.value, searchDate).length === 0;
              return (
                <SearchPickerOption
                  key={opt.value}
                  selected={resolvedShift === opt.value}
                  onSelect={() => handleShiftPick(opt.value)}
                  disabled={blockedToday}
                  disabledHint="No slots left today"
                >
                  {opt.label}
                </SearchPickerOption>
              );
            })}
          </SearchPickerDropdown>
          <SearchFieldDivider />
          <SearchPickerDropdown
            pickerId="search-picker-time"
            open={openPicker === "time"}
            onToggle={setTimeOpen}
            ignoreOutsideUntilRef={ignoreOutsideUntilRef}
            label="Time"
            icon={<SearchIconClock />}
            className="landing-search-unified__field--time"
            prompt={false}
            valueDisplay={
              <div className="landing-search-unified__value-stack">
                <strong className="landing-search-unified__value-main">{timeLabel}</strong>
              </div>
            }
          >
            {searchTimeOptions.map((opt) => (
              <SearchPickerOption
                key={opt.value}
                selected={searchTime === opt.value}
                onSelect={() => handleTimePick(opt.value)}
              >
                {opt.label}
              </SearchPickerOption>
            ))}
          </SearchPickerDropdown>
          <SearchFieldDivider />
          <div className="landing-search-unified__submit-wrap">
            <button
              type="button"
              className="landing-search-unified__submit"
              onClick={() => onSearch?.()}
              disabled={!submitEnabled}
              aria-disabled={!submitEnabled}
              title={submitTitle}
            >
              <SearchIconMagnifier />
              <span>{submitLabel}</span>
            </button>
          </div>
            </div>
          </div>
        </div>
      </div>
      {showHourlyWizard ? (
        <HourlyPreSearchPanel
          packageKey={hourlyPackageKey}
          onPackageKeyChange={onHourlyPackageKeyChange}
          pattern={hourlyPattern}
          onPatternChange={onHourlyPatternChange}
          recurringPattern={hourlyRecurringPattern}
          onRecurringPatternChange={onHourlyRecurringPatternChange}
          searchDate={searchDate}
          onSearchDateChange={onSearchDateChange}
          availableSlots={hourlyAvailableSlots}
          selectedSlot={hourlySelectedSlot}
          onSelectSlot={onHourlySelectSlot}
          slotsReady={hourlySlotsReady}
          loadingSlots={hourlyLoadingSlots}
          onGenerateSlots={onHourlyGenerateSlots}
          canGenerateSlots={canGenerateHourlySlots}
          slotSelected={Boolean(hourlySelectedSlot)}
        />
      ) : null}
    </section>
  );
}

export function formatCaregiverRating(value, reviewCount) {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating <= 0) {
    return reviewCount > 0 ? `New (${reviewCount} review${reviewCount === 1 ? "" : "s"})` : "New";
  }
  const reviews =
    reviewCount > 0 ? ` (${reviewCount} review${reviewCount === 1 ? "" : "s"})` : "";
  return `${rating.toFixed(1)}${reviews}`;
}

export function formatCaregiverMoney(value, suffix = "") {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `₹ ${n.toLocaleString("en-IN")}${suffix}`;
}

export function estimateDailyRate(caregiver) {
  const direct = Number(caregiver.dailyRateExpectation);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const hourly = Number(caregiver.hourlyRateExpectation);
  if (Number.isFinite(hourly) && hourly > 0) return Math.round(hourly * 11);
  const monthly = Number(caregiver.salaryExpectation);
  if (Number.isFinite(monthly) && monthly > 0) return Math.round(monthly / 26);
  return null;
}

export function formatCaregiverHeight(value) {
  const text = String(value || "").trim();
  if (!text) return "—";
  return text;
}

export function formatCaregiverWeight(value) {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return `${n} kg`;
  const text = String(value || "").trim();
  return text || "—";
}

export function formatCaregiverGender(value) {
  const g = String(value || "").trim().toLowerCase();
  if (!g) return "—";
  if (g === "female") return "Female";
  if (g === "male") return "Male";
  return g.charAt(0).toUpperCase() + g.slice(1);
}

export function formatCaregiverDistance(km) {
  const n = Number(km);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1) return `${Math.round(n * 1000)} m away`;
  return `${n.toFixed(1)} km away`;
}

export function MarketplaceResultsToolbar({
  count = 0,
  locationLabel = "",
  sortBy = "recommended",
  onSortChange,
  viewMode = "grid",
  onViewModeChange,
}) {
  const countDisplay =
    count >= 1250 ? "1,250+" : count >= 100 ? `${count.toLocaleString("en-IN")}+` : String(count);
  const sortLabel =
    sortBy === "rating" ? "highest rating" : sortBy === "distance" ? "nearest" : "best match";

  return (
    <div className="landing-results__head">
      <div>
        <h2>{countDisplay} Verified Caregivers Available Near You</h2>
        {locationLabel ? (
          <p className="landing-results__sub">
            Near {locationLabel} · Sorted by {sortLabel}
          </p>
        ) : (
          <p className="landing-results__sub">Sorted by {sortLabel}</p>
        )}
      </div>
      <div className="landing-results__tools">
        <label className="landing-results__sort">
          Sort by:
          <select value={sortBy} onChange={(e) => onSortChange?.(e.target.value)}>
            <option value="recommended">Recommended</option>
            <option value="rating">Highest Rating</option>
            <option value="distance">Nearest</option>
          </select>
        </label>
        <button
          type="button"
          className={`landing-results__map${viewMode === "map" ? " is-active" : ""}`}
          onClick={() => onViewModeChange?.(viewMode === "map" ? "grid" : "map")}
          aria-pressed={viewMode === "map"}
        >
          <span className="landing-results__map-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </span>
          Map View
        </button>
        <div className="landing-results__view-toggle">
          <button
            type="button"
            className={viewMode === "grid" ? "is-active" : ""}
            onClick={() => onViewModeChange?.("grid")}
            aria-label="Grid view"
          >
            ⊞
          </button>
          <button
            type="button"
            className={viewMode === "list" ? "is-active" : ""}
            onClick={() => onViewModeChange?.("list")}
            aria-label="List view"
          >
            ☰
          </button>
        </div>
      </div>
    </div>
  );
}

function WishlistIcon({ filled = false }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 3.5h8a1.5 1.5 0 0 1 1.5 1.5v13.8l-5.5-4.1-5.5 4.1V5a1.5 1.5 0 0 1 1.5-1.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        fill={filled ? "currentColor" : "none"}
      />
    </svg>
  );
}

function CaregiverRatingLine({ rating, reviewCount, formatRating = formatCaregiverRating }) {
  const text = formatRating(rating, reviewCount);
  const match = text.match(/^([\d.]+|New)(.*)$/);
  const value = match ? match[1] : text;
  const reviews = match ? match[2] : "";

  return (
    <p className="landing-card__rating-line">
      <span className="landing-card__rating-star" aria-hidden="true">
        ★
      </span>
      <span className="landing-card__rating-text">
        {value}
        {reviews}
      </span>
    </p>
  );
}

export function CaregiverListingCard({
  caregiver: c,
  getInitials,
  formatRating = formatCaregiverRating,
  formatMoney = formatCaregiverMoney,
  onViewProfile,
  onHire,
  onCall,
  onToggleFav,
  isFav,
  planType = "daily",
  hireLabel = "Hire Now",
}) {
  const verified = isCaregiverVerified(c);
  const skills = (c.skills || []).filter(Boolean);
  const languages = (c.languages || []).filter(Boolean);
  const distanceLabel = formatCaregiverDistance(c.distanceKm);
  const experienceLabel =
    Number.isFinite(Number(c.years)) && Number(c.years) > 0
      ? `${Number(c.years)} Year${Number(c.years) === 1 ? "" : "s"} Experience`
      : "Experience on request";
  const metaParts = [experienceLabel, distanceLabel].filter(Boolean);
  const displaySkills = skills.slice(0, 3);
  const tagsToShow = displaySkills.length
    ? displaySkills
    : ["Elder Care", "Mobility Support", "Daily Assistance"];

  return (
    <article
      className="landing-card landing-card--spec landing-card--clickable"
      onClick={() => onViewProfile?.(c)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onViewProfile?.(c);
        }
      }}
      role="link"
      tabIndex={0}
      aria-label={`View profile for ${c.name}`}
    >
      <button
        type="button"
        className={`landing-card__wishlist${isFav ? " is-on" : ""}`}
        aria-label={isFav ? "Remove from wishlist" : "Add to wishlist"}
        title={isFav ? "In wishlist" : "Add to wishlist"}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFav?.(c);
        }}
      >
        <WishlistIcon filled={isFav} />
      </button>

      <div className="landing-card__head">
        <div className="landing-card__media">
          <ProfileAvatar
            name={c.name}
            imageUrl={c.profileImageUrl}
            storageKey={c.profileImageStorageKey}
            className="landing-card__photo"
            initialsFallback="C"
          />
          <span
            className={`landing-card__availability${c.isAvailable ? " is-available" : " is-unavailable"}`}
          >
            {c.isAvailable ? "Available" : "Unavailable"}
          </span>
        </div>

        <div className="landing-card__intro">
          <div className="landing-card__title-row">
            <h3 className="landing-card__name">{c.name}</h3>
            {verified ? (
              <span className="landing-card__verified" title="Verified caregiver">
                <span className="landing-card__verified-icon" aria-hidden="true">
                  ✓
                </span>
              </span>
            ) : null}
          </div>
          <CaregiverRatingLine rating={c.rating} reviewCount={c.reviewCount} formatRating={formatRating} />
          {metaParts.length ? <p className="landing-card__meta">{metaParts.join(" • ")}</p> : null}
          {languages.length ? (
            <p className="landing-card__langs">Languages: {languages.join(", ")}</p>
          ) : null}
          <div className="landing-card__tags">
            {tagsToShow.map((s) => (
              <span key={s}>{s}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="landing-card__lower">
        <div className="landing-card__stats" aria-label="Caregiver details">
          <div className="landing-card__stat">
            <span className="landing-card__stat-label">Age</span>
            <span className="landing-card__stat-value">{c.age ? `${c.age} Yrs` : "—"}</span>
          </div>
          <div className="landing-card__stat">
            <span className="landing-card__stat-label">Height</span>
            <span className="landing-card__stat-value">{formatCaregiverHeight(c.height)}</span>
          </div>
          <div className="landing-card__stat">
            <span className="landing-card__stat-label">Weight</span>
            <span className="landing-card__stat-value">{formatCaregiverWeight(c.weight)}</span>
          </div>
          <div className="landing-card__stat">
            <span className="landing-card__stat-label">Gender</span>
            <span className="landing-card__stat-value">{formatCaregiverGender(c.gender)}</span>
          </div>
        </div>
      </div>

      <div className="landing-card__footer">
        <div className="landing-card__pricing">
          <p className="landing-card__rate-note" title={formatCaregiverRoleStatusLine(c)}>
            {formatCaregiverRoleStatusLine(c)}
          </p>
        </div>
        <div className="landing-card__actions">
          <button
            type="button"
            className="landing-card__view"
            onClick={(e) => {
              e.stopPropagation();
              onViewProfile(c);
            }}
          >
            View Profile
          </button>
          {onCall ? (
            <button
              type="button"
              className="landing-card__call"
              onClick={(e) => {
                e.stopPropagation();
                onCall(c);
              }}
              disabled={!c.isAvailable}
            >
              Call Me
            </button>
          ) : null}
          <button
            type="button"
            className="landing-card__hire"
            onClick={(e) => {
              e.stopPropagation();
              onHire(c);
            }}
            disabled={!c.isAvailable}
          >
            {hireLabel}
          </button>
        </div>
      </div>
    </article>
  );
}
